<script>
import { mapActions } from 'vuex';

export default {
    props: {
        sections: {
            type: Object,
            required: true
        },

        report_header: {
            type: Object,
            required: true
        },

        selected_language: {
            type: String,
            default: 'en'
        },

        languages: {
            type: String,
            default: 'en'
        },

        show_title: {
            type: Boolean,
            default: false
        },
    },

    data() {
        return {
            current_section_index: 0,

            is_editing: false,
            translate_selection: false,
        }
    },

    computed: {
        key_for_language() {
            if (this.selected_language === 'en') return '';
            return `_${this.selected_language}`;
        },

        type() {
            return this.report_header.report_header_element_id != null ? 'element_section' : 'report_section';
        },
    },

    mounted() {

    },

    methods: {
        ...mapActions('reports', [
            'updateSustSection',
            'deleteSustSection',
            'selectSustSection',
            'updateElementSection',
            'deleteElementSection',
            'selectElementSection',
            'translateSection',
        ]),

        updateSection() {
            if (this.translate_selection) {
                return this._translateSection();
            }

            const params = {
                id: null,
                body: {
                    [`generated_text${this.key_for_language}`]: this.sections[this.current_section_index][`generated_text${this.key_for_language}`],
                    section_language: 'en-tr',
                    updated_by: this.$user.user_id,
                }
            };

            let api_call = null;

            if (this.type === 'element_section') {
                params.id = this.sections[this.current_section_index].element_section_id;
                params[`generated_header${this.key_for_language}`] = this.sections[this.current_section_index][`generated_header${this.key_for_language}`];
                api_call = this.updateElementSection;
            } else if (this.type === 'report_section') {
                api_call = this.updateSustSection;
                params.id = this.sections[this.current_section_index].sust_report_section_id;
            } else return;

            if (this.selected_language === 'en' && this.languages.includes('tr')) {
                params.body.section_language = 'en-tr';
            } else if (this.selected_language === 'en' && !this.languages.includes('tr')) {
                params.body.section_language = 'en';
            } else if (this.selected_language === 'tr' && this.languages.includes('en')) {
                params.body.section_language = 'tr-en';
            } else if (this.selected_language === 'tr' && !this.languages.includes('en')) {
                params.body.section_language = 'tr';
            }

            api_call(params)
                .then((res) => {
                    this.$notify('success', res.data.messageName);
                })
                .catch(({ response }) => {
                    this.$notify('error', response.data.messageName);
                })
                .finally(() => {
                    this.is_editing = false;
                })
        },

        async _translateSection() {
            if (!this.sections[this.current_section_index]) {
                console.error('There was an error translating the generated section.');
                return;
            }
            
            const sect = this.sections[this.current_section_index];
            const id = sect.element_section_id ? sect.element_section_id : sect.sust_report_section_id;

            const generated_text_key = `generated_text${this.key_for_language}`;
            const section_language = this.selected_language === 'tr' ? 'tr-en' : 'en-tr';

            const params = {
                type: this.type,
                id: id,
                body: {
                    [generated_text_key]: sect[generated_text_key],
                    section_language: section_language,
                    updated_by: this.$user.user_id,
                }
            }

            if (this.type === 'element_section') {
                params.body['generated_header'] = this.sections[this.current_section_index].generated_header;
            }

            this.translateSection(params)
                .then(() => {
                    this.$notify('success', '', true);
                })
                .catch(() => {
                    this.$notify('error', '', true);
                })
        },

        deleteSection() {
            const params = {
                element_section_id: null,
                sust_report_section_id: null, 
            };

            let api_call = null;

            if (this.type === 'element_section') {
                api_call = this.deleteElementSection;
                params.element_section_id = this.sections[this.current_section_index].element_section_id;
            } else if (this.type === 'report_section') {
                api_call = this.deleteSustSection;
                params.sust_report_section_id = this.sections[this.current_section_index].sust_report_section_id;
            } else return;

            api_call(params)
                .then((res) => {
                    this.$notify('success', res.data.messageName);
                    if (params.element_section_id != null) {
                        this.sections = this.sections.filter(x => x.element_section_id != params.element_section_id);
                    } else if (params.sust_report_section_id != null) {
                        this.sections = this.sections.filter(x => x.sust_report_section_id != params.sust_report_section_id);
                    }
                    this.$emit('delete', this.sections);
                })
                .catch(({ response }) => {
                    this.$notify('error', response.data.messageName);
                })
        },

        setCurrentSectionIndex(idx, created_sections) {
            if (idx < 0 || idx >= created_sections.length) 
                return;

            this.current_section_index = idx;
            this.$emit('changeIndex', idx);
        },

        changeTranslateSectionState(e) {
            this.translate_selection = e;
        },

        handleSelectSection() {
            const api_call = this.type === 'element_section' ? this.selectElementSection : (this.type === 'report_section' ? this.selectSustSection : null);
            const body_key = this.type === 'element_section' ? 'element_section_id' : (this.type === 'report_section' ? 'sust_report_section_id' : null);
            if (!api_call || !body_key) return;

            api_call({id: this.sections[this.current_section_index][body_key]})
                .then(() => {
                    const idx = this.sections.findIndex(x => x.is_selected === true);
                    if (idx != -1) {
                        this.sections[idx].is_selected = false;
                    }

                    this.sections[this.current_section_index].is_selected = true;

                    this.$notify('success', '', true);
                })
                .catch(({ response }) => {
                    this.$notify('error', response.data.messageName);
                })
        },
    }
}
</script>

<template>
    <div v-if="sections.length > 0" 
        class="flex-all-centered-col"
    >
        <div class="flex-all-centered-row" 
            :style="{
                'justify-content': show_title ? 'space-between' : 'flex-end',
                'border-bottom': show_title ? '.15em solid var(--sustainfinity-navy)' : null,
            }"
        >
            <div v-if="show_title"
                class="report-header-name"
                style="flex: 1;"
            >
                {{ report_header.report_header_name }}
            </div>

            <div class="flex-all-centered-row" style="justify-content: flex-end; flex: 1;">
                <div style="margin: 0 .5em; color: var(--sustainfinity-navy);">
                    {{ sections[current_section_index][`created_on`] | dateFormat }}
                </div>
                
                <button v-if="!is_editing"
                    class="icon-button" 
                    @click="() => is_editing = true"
                >
                    <i class="fa-solid fa-pen-to-square" style="color: var(--sustainfinity-blue);"></i>
                </button>

                <button v-if="!is_editing"
                    class="icon-button"
                    @click="() => $refs.deletePopup.openPopup()"
                >
                    <i class="fa-solid fa-trash" style="color: var(--invalid-input);"></i>
                </button>
            </div>
        </div>

        <div class="flex-all-centered-col" style="margin-top: .25em;">
            <input v-if="sections[current_section_index][`generated_header${key_for_language}`] != null"
                class="interactive-text-field"
                type="text" 
                v-model="sections[current_section_index][`generated_header${key_for_language}`]"
                :disabled="!is_editing"
            >

            <textarea 
                class="interactive-text-field"
                rows="20"
                v-model="sections[current_section_index][`generated_text${key_for_language}`]"
                :disabled="!is_editing"
            ></textarea>

            <div v-if="is_editing && selected_language && languages.length > 1"
                class="flex-all-centered-row" 
                style="justify-content: flex-start !important;"            
            >
                <switch-button 
                    :initialState="translate_selection"
                    @buttonState="changeTranslateSectionState($event)" 
                />
                <div style="color: var(--invalid-input) !important; font-size: bolder; font-size: 1.1em;">
                    {{ $t('generateSections.confirm_translate') }}
                </div>
            </div>

            <div class="flex-all-centered-row" style="justify-content: flex-start;">
                <input type="checkbox"
                    id="select-text" 
                    style="margin-right: .5em;"
                    :class="{ 'disabled-buttons': sections[current_section_index].is_selected }"
                    :checked="sections[current_section_index].is_selected"
                    :disabled="sections[current_section_index].is_selected"
                    @click="handleSelectSection()"
                >
                <label for="select-text" style="margin: 0;">
                    {{ $t('generateSections.buttons.select_text') }}
                </label>
            </div>

            <div v-if="is_editing"
                class="flex-all-centered-row"
                style="justify-content: flex-end;"
            >
                <button class="sust-btn sust-btn-red"
                    style="margin-left: 1em; margin-top: .5em;"
                    @click="() => is_editing = false"
                >
                    {{ $t('misc.cancel') }}
                </button>
                <button class="sust-btn sust-btn-green"
                    style="margin-left: 1em; margin-top: .5em;"
                    @click="updateSection()"
                >
                    {{ $t('misc.save') }}
                </button>
            </div>

            <div class="flex-all-centered-row">
                <div style="flex: 1; border-bottom: .2em solid var(--sustainfinity-navy);"></div>
                <div style="flex: 1"
                    class="flex-all-centered-row"
                >
                    <input v-for="(_, idx) in sections" :key="idx"
                        type="radio"
                        :name="`current_section_index_switch_${report_header.report_header_order}`"
                        :checked="idx === current_section_index"
                        @click="() => current_section_index = idx"
                    >
                </div>
                <div style="flex: 1; border-bottom: .2em solid var(--sustainfinity-navy);"></div>
            </div>
        </div>

        <sust-popup ref="deletePopup"
            size="xs"
            :title="$t('section_generation.delete_section_title')"
            @ok="deleteSection()"
        >
            {{ $t('section_generation.delete_section_text') }}
        </sust-popup>
    </div>
</template>

<style lang='scss' scoped>
    .report-header-name {
        font-size: 1.5em;
        font-weight: bold;
        text-align: left;
        color: var(--sustainfinity-navy);
        justify-content: flex-start;
        width: fit-content;
    }

    .interactive-text-field {
        width: 100% !important;
        min-height: 2em;
        padding: .5em;
        margin-bottom: .5em;
        border: 0 !important;
        outline: thin solid gray;
        border-radius: .2em;
        color: var(--sustainfinity-navy);

        &:disabled {
            background-color: transparent !important;
            outline: 0 !important;
            cursor: text;
        }
    }

    input[type="checkbox"]:checked {
        filter: hue-rotate(-90deg);
    }

    input[type="radio"] {
        cursor: pointer;
        &:checked {
            filter: brightness(60%);
        }
    }
</style>